import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Book, Plus, FileText, Lightbulb, ChevronRight, ChevronDown, Edit2, Trash2, Download, ArrowLeft, Save, Printer, BookOpen, GripVertical } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [proyectos, setProyectos] = useState([]);
  const [nuevoProyecto, setNuevoProyecto] = useState({ titulo: "", descripcion: "" });
  const [dialogAbierto, setDialogAbierto] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    cargarProyectos();
  }, []);

  const cargarProyectos = async () => {
    try {
      const response = await axios.get(`${API}/proyectos`);
      setProyectos(response.data);
    } catch (e) {
      toast.error("Error al cargar proyectos");
    }
  };

  const crearProyecto = async () => {
    if (!nuevoProyecto.titulo.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    try {
      await axios.post(`${API}/proyectos`, nuevoProyecto);
      toast.success("Proyecto creado");
      setNuevoProyecto({ titulo: "", descripcion: "" });
      setDialogAbierto(false);
      cargarProyectos();
    } catch (e) {
      toast.error("Error al crear proyecto");
    }
  };

  const eliminarProyecto = async (id) => {
    try {
      await axios.delete(`${API}/proyectos/${id}`);
      toast.success("Proyecto eliminado");
      cargarProyectos();
    } catch (e) {
      toast.error("Error al eliminar proyecto");
    }
  };

  const limpiarCache = async () => {
    try {
      // Limpiar todas las cachés
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      
      // Desregistrar service workers
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
      
      toast.success("Caché limpiada. Recargando...");
      
      // Recargar la página
      setTimeout(() => {
        window.location.reload(true);
      }, 1000);
    } catch (e) {
      toast.error("Error al limpiar caché");
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-4xl font-bold text-neutral-900 mb-2">Mis Proyectos Literarios</h1>
            <p className="text-neutral-600">Organiza tus historias, borradores y notas</p>
          </div>
          <div className="flex gap-2">
            <Button 
              data-testid="limpiar-cache-btn" 
              variant="outline" 
              onClick={limpiarCache}
              className="gap-2"
            >
              🔄 Sincronizar
            </Button>
            <Dialog open={dialogAbierto} onOpenChange={setDialogAbierto}>
              <DialogTrigger asChild>
                <Button data-testid="crear-proyecto-btn" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Nuevo Proyecto
                </Button>
              </DialogTrigger>
              <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Nuevo Proyecto</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-1 block">Título</label>
                  <Input
                    data-testid="proyecto-titulo-input"
                    placeholder="Ej: Mi Primera Novela"
                    value={nuevoProyecto.titulo}
                    onChange={(e) => setNuevoProyecto({ ...nuevoProyecto, titulo: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-1 block">Descripción</label>
                  <Textarea
                    data-testid="proyecto-descripcion-input"
                    placeholder="Breve descripción del proyecto..."
                    value={nuevoProyecto.descripcion}
                    onChange={(e) => setNuevoProyecto({ ...nuevoProyecto, descripcion: e.target.value })}
                    rows={3}
                  />
                </div>
                <Button data-testid="guardar-proyecto-btn" onClick={crearProyecto} className="w-full">
                  Crear Proyecto
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {proyectos.length === 0 ? (
          <div className="text-center py-20">
            <Book className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500 text-lg mb-4">No hay proyectos aún</p>
            <p className="text-neutral-400 text-sm">Crea tu primer proyecto literario para comenzar</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {proyectos.map((proyecto) => (
              <div
                key={proyecto.id}
                data-testid={`proyecto-card-${proyecto.id}`}
                className="bg-white border border-neutral-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/proyecto/${proyecto.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <Book className="w-8 h-8 text-neutral-700" />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        data-testid={`eliminar-proyecto-btn-${proyecto.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar proyecto?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta acción eliminará el proyecto y todo su contenido.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid={`confirmar-eliminar-${proyecto.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            eliminarProyecto(proyecto.id);
                          }}
                        >
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">{proyecto.titulo}</h3>
                <p className="text-neutral-600 text-sm line-clamp-2">{proyecto.descripcion || "Sin descripción"}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente sortable para borradores
const SortableBorrador = ({ borrador, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: borrador.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-2">
        <button
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-neutral-100 rounded"
          data-testid={`drag-borrador-${borrador.id}`}
        >
          <GripVertical className="w-4 h-4 text-neutral-400" />
        </button>
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};

// Componente sortable para capítulos
const SortableCapitulo = ({ capitulo, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: capitulo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-2">
        <button
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-neutral-100 rounded"
          data-testid={`drag-capitulo-${capitulo.id}`}
        >
          <GripVertical className="w-3 h-3 text-neutral-400" />
        </button>
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};

// Componente sortable para escenas
const SortableEscena = ({ escena, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: escena.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-1">
        <button
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-neutral-100 rounded"
          data-testid={`drag-escena-${escena.id}`}
        >
          <GripVertical className="w-3 h-3 text-neutral-400" />
        </button>
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};

const ProyectoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proyecto, setProyecto] = useState(null);
  const [borradores, setBorradores] = useState([]);
  const [notas, setNotas] = useState([]);
  const [maestros, setMaestros] = useState([]);
  const [expandidos, setExpandidos] = useState({});
  const [capitulos, setCapitulos] = useState({});
  const [escenas, setEscenas] = useState({});
  const [dialogBorrador, setDialogBorrador] = useState(false);
  const [dialogCapitulo, setDialogCapitulo] = useState({ abierto: false, borradorId: null });
  const [dialogEscena, setDialogEscena] = useState({ abierto: false, capituloId: null });
  const [dialogNota, setDialogNota] = useState(false);
  const [dialogMaestro, setDialogMaestro] = useState(false);
  const [nuevoBorrador, setNuevoBorrador] = useState({ titulo: "" });
  const [nuevoCapitulo, setNuevoCapitulo] = useState({ titulo: "" });
  const [nuevaEscena, setNuevaEscena] = useState({ titulo: "" });
  const [nuevaNota, setNuevaNota] = useState({ titulo: "", contenido: "" });
  const [nuevoMaestro, setNuevoMaestro] = useState({ titulo: "", tipo: "general", contenido: "" });
  const [editandoProyecto, setEditandoProyecto] = useState(false);
  const [tituloProyectoEdit, setTituloProyectoEdit] = useState("");
  const [descripcionProyectoEdit, setDescripcionProyectoEdit] = useState("");
  const [editandoBorrador, setEditandoBorrador] = useState(null);
  const [tituloBorradorEdit, setTituloBorradorEdit] = useState("");
  const [editandoCapitulo, setEditandoCapitulo] = useState(null);
  const [tituloCapituloEdit, setTituloCapituloEdit] = useState("");
  const [prologo, setPrologo] = useState("");
  const [epilogo, setEpilogo] = useState("");
  const [autor, setAutor] = useState("");

  // Sensors para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    cargarProyecto();
  }, [id]);

  const cargarProyecto = async () => {
    try {
      const [proyectoRes, borradoresRes, notasRes, maestrosRes] = await Promise.all([
        axios.get(`${API}/proyectos/${id}`),
        axios.get(`${API}/borradores/proyecto/${id}`),
        axios.get(`${API}/notas/proyecto/${id}`),
        axios.get(`${API}/maestros/proyecto/${id}`),
      ]);
      setProyecto(proyectoRes.data);
      setBorradores(borradoresRes.data);
      setNotas(notasRes.data);
      setMaestros(maestrosRes.data);
      setPrologo(proyectoRes.data.prologo || "");
      setEpilogo(proyectoRes.data.epilogo || "");
      setAutor(proyectoRes.data.autor || "");
    } catch (e) {
      toast.error("Error al cargar proyecto");
    }
  };

  const editarProyecto = async () => {
    try {
      await axios.put(`${API}/proyectos/${id}`, {
        titulo: tituloProyectoEdit,
        descripcion: descripcionProyectoEdit,
      });
      toast.success("Proyecto actualizado");
      setEditandoProyecto(false);
      cargarProyecto();
    } catch (e) {
      toast.error("Error al actualizar proyecto");
    }
  };

  const editarBorrador = async (borradorId) => {
    try {
      await axios.put(`${API}/borradores/${borradorId}`, {
        titulo: tituloBorradorEdit,
      });
      toast.success("Borrador actualizado");
      setEditandoBorrador(null);
      cargarProyecto();
    } catch (e) {
      toast.error("Error al actualizar borrador");
    }
  };

  const editarCapitulo = async (capituloId) => {
    try {
      await axios.put(`${API}/capitulos/${capituloId}`, {
        titulo: tituloCapituloEdit,
      });
      toast.success("Capítulo actualizado");
      setEditandoCapitulo(null);
      // Recargar capítulos del borrador correspondiente
      const capitulo = Object.values(capitulos).flat().find(c => c.id === capituloId);
      if (capitulo) {
        cargarCapitulos(capitulo.borrador_id);
      }
    } catch (e) {
      toast.error("Error al actualizar capítulo");
    }
  };

  const imprimirProyecto = () => {
    window.print();
  };

  const guardarExtras = async () => {
    try {
      await axios.put(`${API}/proyectos/${id}`, {
        prologo,
        epilogo,
        autor
      });
      toast.success("Prólogo y epílogo guardados");
      cargarProyecto();
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  // Función para reordenar borradores
  const handleDragEndBorradores = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = borradores.findIndex((b) => b.id === active.id);
    const newIndex = borradores.findIndex((b) => b.id === over.id);
    
    const newBorradores = arrayMove(borradores, oldIndex, newIndex);
    setBorradores(newBorradores);
    
    // Actualizar orden en backend
    try {
      for (let i = 0; i < newBorradores.length; i++) {
        await axios.put(`${API}/borradores/${newBorradores[i].id}`, {
          orden: i
        });
      }
      toast.success("Orden actualizado");
    } catch (e) {
      toast.error("Error al actualizar orden");
      cargarProyecto();
    }
  };

  // Función para reordenar capítulos
  const handleDragEndCapitulos = async (event, borradorId) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const caps = capitulos[borradorId] || [];
    const oldIndex = caps.findIndex((c) => c.id === active.id);
    const newIndex = caps.findIndex((c) => c.id === over.id);
    
    const newCapitulos = arrayMove(caps, oldIndex, newIndex);
    setCapitulos((prev) => ({ ...prev, [borradorId]: newCapitulos }));
    
    // Actualizar orden en backend
    try {
      for (let i = 0; i < newCapitulos.length; i++) {
        await axios.put(`${API}/capitulos/${newCapitulos[i].id}`, {
          orden: i
        });
      }
      toast.success("Orden actualizado");
    } catch (e) {
      toast.error("Error al actualizar orden");
      cargarCapitulos(borradorId);
    }
  };

  // Función para reordenar escenas
  const handleDragEndEscenas = async (event, capituloId) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const escs = escenas[capituloId] || [];
    const oldIndex = escs.findIndex((e) => e.id === active.id);
    const newIndex = escs.findIndex((e) => e.id === over.id);
    
    const newEscenas = arrayMove(escs, oldIndex, newIndex);
    setEscenas((prev) => ({ ...prev, [capituloId]: newEscenas }));
    
    // Actualizar orden en backend
    try {
      for (let i = 0; i < newEscenas.length; i++) {
        await axios.put(`${API}/escenas/${newEscenas[i].id}`, {
          orden: i
        });
      }
      toast.success("Orden actualizado");
    } catch (e) {
      toast.error("Error al actualizar orden");
      cargarEscenas(capituloId);
    }
  };

  const cargarCapitulos = async (borradorId) => {
    try {
      const response = await axios.get(`${API}/capitulos/borrador/${borradorId}`);
      setCapitulos((prev) => ({ ...prev, [borradorId]: response.data }));
    } catch (e) {
      toast.error("Error al cargar capítulos");
    }
  };

  const cargarEscenas = async (capituloId) => {
    try {
      const response = await axios.get(`${API}/escenas/capitulo/${capituloId}`);
      setEscenas((prev) => ({ ...prev, [capituloId]: response.data }));
    } catch (e) {
      toast.error("Error al cargar escenas");
    }
  };

  const toggleExpandir = (borradorId) => {
    const nuevoEstado = !expandidos[borradorId];
    setExpandidos({ ...expandidos, [borradorId]: nuevoEstado });
    if (nuevoEstado && !capitulos[borradorId]) {
      cargarCapitulos(borradorId);
    }
  };

  const toggleExpandirCapitulo = (capituloId) => {
    const nuevoEstado = !expandidos[`cap-${capituloId}`];
    setExpandidos({ ...expandidos, [`cap-${capituloId}`]: nuevoEstado });
    if (nuevoEstado && !escenas[capituloId]) {
      cargarEscenas(capituloId);
    }
  };

  const crearBorrador = async () => {
    if (!nuevoBorrador.titulo.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    try {
      await axios.post(`${API}/borradores`, {
        proyecto_id: id,
        titulo: nuevoBorrador.titulo,
        orden: borradores.length,
      });
      toast.success("Borrador creado");
      setNuevoBorrador({ titulo: "" });
      setDialogBorrador(false);
      cargarProyecto();
    } catch (e) {
      toast.error("Error al crear borrador");
    }
  };

  const crearCapitulo = async () => {
    if (!nuevoCapitulo.titulo.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    try {
      const borradorId = dialogCapitulo.borradorId;
      const caps = capitulos[borradorId] || [];
      await axios.post(`${API}/capitulos`, {
        borrador_id: borradorId,
        titulo: nuevoCapitulo.titulo,
        orden: caps.length,
      });
      toast.success("Capítulo creado");
      setNuevoCapitulo({ titulo: "" });
      setDialogCapitulo({ abierto: false, borradorId: null });
      cargarCapitulos(borradorId);
    } catch (e) {
      toast.error("Error al crear capítulo");
    }
  };

  const crearEscena = async () => {
    if (!nuevaEscena.titulo.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    try {
      const capituloId = dialogEscena.capituloId;
      const escs = escenas[capituloId] || [];
      const response = await axios.post(`${API}/escenas`, {
        capitulo_id: capituloId,
        titulo: nuevaEscena.titulo,
        orden: escs.length,
      });
      toast.success("Escena creada");
      setNuevaEscena({ titulo: "" });
      setDialogEscena({ abierto: false, capituloId: null });
      cargarEscenas(capituloId);
      navigate(`/escena/${response.data.id}`);
    } catch (e) {
      toast.error("Error al crear escena");
    }
  };

  const crearNota = async () => {
    if (!nuevaNota.titulo.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    try {
      await axios.post(`${API}/notas`, {
        proyecto_id: id,
        titulo: nuevaNota.titulo,
        contenido: nuevaNota.contenido,
      });
      toast.success("Nota creada");
      setNuevaNota({ titulo: "", contenido: "" });
      setDialogNota(false);
      cargarProyecto();
    } catch (e) {
      toast.error("Error al crear nota");
    }
  };

  const crearMaestro = async () => {
    if (!nuevoMaestro.titulo.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    try {
      await axios.post(`${API}/maestros`, {
        proyecto_id: id,
        titulo: nuevoMaestro.titulo,
        tipo: nuevoMaestro.tipo,
        contenido: nuevoMaestro.contenido,
      });
      toast.success("Guía creada");
      setNuevoMaestro({ titulo: "", tipo: "general", contenido: "" });
      setDialogMaestro(false);
      cargarProyecto();
    } catch (e) {
      toast.error("Error al crear guía");
    }
  };

  const eliminarNota = async (notaId) => {
    try {
      await axios.delete(`${API}/notas/${notaId}`);
      toast.success("Nota eliminada");
      cargarProyecto();
    } catch (e) {
      toast.error("Error al eliminar nota");
    }
  };

  const eliminarMaestro = async (maestroId) => {
    try {
      await axios.delete(`${API}/maestros/${maestroId}`);
      toast.success("Guía eliminada");
      cargarProyecto();
    } catch (e) {
      toast.error("Error al eliminar guía");
    }
  };

  const eliminarBorrador = async (borradorId) => {
    try {
      await axios.delete(`${API}/borradores/${borradorId}`);
      toast.success("Borrador eliminado");
      cargarProyecto();
    } catch (e) {
      toast.error("Error al eliminar borrador");
    }
  };

  const eliminarCapitulo = async (capituloId, borradorId) => {
    try {
      await axios.delete(`${API}/capitulos/${capituloId}`);
      toast.success("Capítulo eliminado");
      cargarCapitulos(borradorId);
    } catch (e) {
      toast.error("Error al eliminar capítulo");
    }
  };

  const eliminarEscena = async (escenaId, capituloId) => {
    try {
      await axios.delete(`${API}/escenas/${escenaId}`);
      toast.success("Escena eliminada");
      cargarEscenas(capituloId);
    } catch (e) {
      toast.error("Error al eliminar escena");
    }
  };

  const exportarProyecto = async () => {
    try {
      const response = await axios.get(`${API}/exportar/${id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${proyecto.titulo}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Proyecto exportado");
    } catch (e) {
      toast.error("Error al exportar proyecto");
    }
  };

  if (!proyecto) return <div className="flex items-center justify-center min-h-screen">Cargando...</div>;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-7xl mx-auto p-8">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <Button data-testid="volver-btn" variant="ghost" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            {editandoProyecto ? (
              <div className="flex-1 space-y-2">
                <Input
                  data-testid="editar-proyecto-titulo-input"
                  value={tituloProyectoEdit}
                  onChange={(e) => setTituloProyectoEdit(e.target.value)}
                  className="text-2xl font-bold"
                />
                <Textarea
                  data-testid="editar-proyecto-descripcion-input"
                  value={descripcionProyectoEdit}
                  onChange={(e) => setDescripcionProyectoEdit(e.target.value)}
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button data-testid="guardar-edicion-proyecto-btn" size="sm" onClick={editarProyecto}>
                    <Save className="w-4 h-4 mr-1" />
                    Guardar
                  </Button>
                  <Button data-testid="cancelar-edicion-proyecto-btn" size="sm" variant="outline" onClick={() => setEditandoProyecto(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div>
                  <h1 className="text-3xl font-bold text-neutral-900">{proyecto.titulo}</h1>
                  <p className="text-neutral-600 mt-1">{proyecto.descripcion}</p>
                </div>
                <Button
                  data-testid="editar-proyecto-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditandoProyecto(true);
                    setTituloProyectoEdit(proyecto.titulo);
                    setDescripcionProyectoEdit(proyecto.descripcion);
                  }}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button 
              data-testid="ver-libro-btn" 
              onClick={() => navigate(`/libro/${id}`)} 
              variant="outline" 
              className="gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Ver como Libro
            </Button>
            <Button data-testid="imprimir-btn" onClick={imprimirProyecto} variant="outline" className="gap-2">
              <Printer className="w-4 h-4" />
              Imprimir
            </Button>
            <Button data-testid="exportar-btn" onClick={exportarProyecto} className="gap-2">
              <Download className="w-4 h-4" />
              Exportar a Word
            </Button>
          </div>
        </div>

        <Tabs defaultValue="borradores" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger data-testid="tab-borradores" value="borradores">
              <FileText className="w-4 h-4 mr-2" />
              Borradores
            </TabsTrigger>
            <TabsTrigger data-testid="tab-extras" value="extras">
              <BookOpen className="w-4 h-4 mr-2" />
              Prólogo/Epílogo
            </TabsTrigger>
            <TabsTrigger data-testid="tab-notas" value="notas">
              <Edit2 className="w-4 h-4 mr-2" />
              Notas
            </TabsTrigger>
            <TabsTrigger data-testid="tab-maestros" value="maestros">
              <Lightbulb className="w-4 h-4 mr-2" />
              Guías
            </TabsTrigger>
          </TabsList>

          <TabsContent value="borradores">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-neutral-900">Estructura del Proyecto</h2>
                <Dialog open={dialogBorrador} onOpenChange={setDialogBorrador}>
                  <DialogTrigger asChild>
                    <Button data-testid="crear-borrador-btn" size="sm" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Nuevo Borrador
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crear Borrador</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Input
                        data-testid="borrador-titulo-input"
                        placeholder="Título del borrador"
                        value={nuevoBorrador.titulo}
                        onChange={(e) => setNuevoBorrador({ titulo: e.target.value })}
                      />
                      <Button data-testid="guardar-borrador-btn" onClick={crearBorrador} className="w-full">
                        Crear
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {borradores.length === 0 ? (
                <p className="text-neutral-500 text-center py-8">No hay borradores aún</p>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndBorradores}>
                  <SortableContext items={borradores.map(b => b.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {borradores.map((borrador) => (
                        <SortableBorrador key={borrador.id} borrador={borrador}>
                          <div className="border border-neutral-200 rounded-lg">
                      <div className="flex items-center justify-between p-4 hover:bg-neutral-50">
                        {editandoBorrador === borrador.id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              data-testid={`editar-borrador-input-${borrador.id}`}
                              value={tituloBorradorEdit}
                              onChange={(e) => setTituloBorradorEdit(e.target.value)}
                              className="flex-1"
                            />
                            <Button
                              data-testid={`guardar-borrador-edit-btn-${borrador.id}`}
                              size="sm"
                              onClick={() => editarBorrador(borrador.id)}
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditandoBorrador(null)}
                            >
                              Cancelar
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div
                              className="flex items-center gap-2 flex-1 cursor-pointer"
                              onClick={() => toggleExpandir(borrador.id)}
                            >
                              {expandidos[borrador.id] ? (
                                <ChevronDown className="w-4 h-4 text-neutral-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-neutral-500" />
                              )}
                              <FileText className="w-5 h-5 text-neutral-700" />
                              <span className="font-medium text-neutral-900">{borrador.titulo}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                data-testid={`editar-borrador-btn-${borrador.id}`}
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditandoBorrador(borrador.id);
                                  setTituloBorradorEdit(borrador.titulo);
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                data-testid={`crear-capitulo-btn-${borrador.id}`}
                                size="sm"
                                variant="ghost"
                                onClick={() => setDialogCapitulo({ abierto: true, borradorId: borrador.id })}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button data-testid={`eliminar-borrador-btn-${borrador.id}`} size="sm" variant="ghost">
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>¿Eliminar borrador?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Se eliminará el borrador y todo su contenido.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => eliminarBorrador(borrador.id)}>
                                      Eliminar
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </>
                        )}
                      </div>

                      {expandidos[borrador.id] && (
                        <div className="px-4 pb-4 pl-10">
                          {capitulos[borrador.id] && capitulos[borrador.id].length > 0 ? (
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEndCapitulos(e, borrador.id)}>
                              <SortableContext items={capitulos[borrador.id].map(c => c.id)} strategy={verticalListSortingStrategy}>
                                {capitulos[borrador.id].map((capitulo) => (
                                  <SortableCapitulo key={capitulo.id} capitulo={capitulo}>
                                    <div className="border-l-2 border-neutral-200 pl-4 py-2">
                                <div className="flex items-center justify-between">
                                  {editandoCapitulo === capitulo.id ? (
                                    <div className="flex items-center gap-2 flex-1">
                                      <Input
                                        data-testid={`editar-capitulo-input-${capitulo.id}`}
                                        value={tituloCapituloEdit}
                                        onChange={(e) => setTituloCapituloEdit(e.target.value)}
                                        className="flex-1"
                                        size="sm"
                                      />
                                      <Button
                                        data-testid={`guardar-capitulo-edit-btn-${capitulo.id}`}
                                        size="sm"
                                        onClick={() => editarCapitulo(capitulo.id)}
                                      >
                                        <Save className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditandoCapitulo(null)}
                                      >
                                        <span className="text-xs">Cancelar</span>
                                      </Button>
                                    </div>
                                  ) : (
                                    <>
                                      <div
                                        className="flex items-center gap-2 flex-1 cursor-pointer"
                                        onClick={() => toggleExpandirCapitulo(capitulo.id)}
                                      >
                                        {expandidos[`cap-${capitulo.id}`] ? (
                                          <ChevronDown className="w-4 h-4 text-neutral-500" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-neutral-500" />
                                        )}
                                        <span className="text-neutral-800">{capitulo.titulo}</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          data-testid={`editar-capitulo-btn-${capitulo.id}`}
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => {
                                            setEditandoCapitulo(capitulo.id);
                                            setTituloCapituloEdit(capitulo.titulo);
                                          }}
                                        >
                                          <Edit2 className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          data-testid={`crear-escena-btn-${capitulo.id}`}
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setDialogEscena({ abierto: true, capituloId: capitulo.id })}
                                        >
                                          <Plus className="w-3 h-3" />
                                        </Button>
                                        <AlertDialog>
                                          <AlertDialogTrigger asChild>
                                            <Button data-testid={`eliminar-capitulo-btn-${capitulo.id}`} size="sm" variant="ghost">
                                              <Trash2 className="w-3 h-3 text-red-500" />
                                            </Button>
                                          </AlertDialogTrigger>
                                          <AlertDialogContent>
                                            <AlertDialogHeader>
                                              <AlertDialogTitle>¿Eliminar capítulo?</AlertDialogTitle>
                                              <AlertDialogDescription>
                                                Se eliminará el capítulo y todas sus escenas.
                                              </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                              <AlertDialogAction onClick={() => {
                                                eliminarCapitulo(capitulo.id, capitulo.borrador_id);
                                              }}>
                                                Eliminar
                                              </AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>
                                      </div>
                                    </>
                                  )}
                                </div>

                                {expandidos[`cap-${capitulo.id}`] && (
                                  <div className="ml-6 mt-2 space-y-1">
                                    {escenas[capitulo.id] && escenas[capitulo.id].length > 0 ? (
                                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEndEscenas(e, capitulo.id)}>
                                        <SortableContext items={escenas[capitulo.id].map(e => e.id)} strategy={verticalListSortingStrategy}>
                                          {escenas[capitulo.id].map((escena) => (
                                            <SortableEscena key={escena.id} escena={escena}>
                                              <div
                                                data-testid={`escena-item-${escena.id}`}
                                                className="flex items-center justify-between py-1 px-2 hover:bg-neutral-100 rounded"
                                              >
                                          <span 
                                            className="text-sm text-neutral-700 flex-1 cursor-pointer"
                                            onClick={() => navigate(`/escena/${escena.id}`)}
                                          >
                                            {escena.titulo}
                                          </span>
                                          <div className="flex items-center gap-1">
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => navigate(`/escena/${escena.id}`)}
                                              className="h-6 w-6 p-0"
                                            >
                                              <Edit2 className="w-3 h-3 text-neutral-400" />
                                            </Button>
                                            <AlertDialog>
                                              <AlertDialogTrigger asChild>
                                                <Button
                                                  data-testid={`eliminar-escena-btn-${escena.id}`}
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-6 w-6 p-0"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <Trash2 className="w-3 h-3 text-red-500" />
                                                </Button>
                                              </AlertDialogTrigger>
                                              <AlertDialogContent>
                                                <AlertDialogHeader>
                                                  <AlertDialogTitle>¿Eliminar escena?</AlertDialogTitle>
                                                  <AlertDialogDescription>
                                                    Se eliminará la escena "{escena.titulo}".
                                                  </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                  <AlertDialogAction onClick={() => eliminarEscena(escena.id, capitulo.id)}>
                                                    Eliminar
                                                  </AlertDialogAction>
                                                </AlertDialogFooter>
                                              </AlertDialogContent>
                                            </AlertDialog>
                                          </div>
                                        </div>
                                      </SortableEscena>
                                    ))}
                                  </SortableContext>
                                </DndContext>
                              ) : (
                                <p className="text-sm text-neutral-400 ml-2">No hay escenas</p>
                              )}
                            </div>
                          )}
                                    </div>
                                  </SortableCapitulo>
                                ))}
                              </SortableContext>
                            </DndContext>
                          ) : (
                            <p className="text-sm text-neutral-400 ml-2">No hay capítulos</p>
                          )}
                        </div>
                      )}
                          </div>
                        </SortableBorrador>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </TabsContent>

          <TabsContent value="extras">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-neutral-900">Prólogo, Epílogo y Autor</h2>
                <Button data-testid="guardar-extras-btn" onClick={guardarExtras} className="gap-2">
                  <Save className="w-4 h-4" />
                  Guardar
                </Button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-2 block">
                    Nombre del Autor
                  </label>
                  <Input
                    data-testid="autor-input"
                    placeholder="Tu nombre como autor..."
                    value={autor}
                    onChange={(e) => setAutor(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-2 block">
                    Prólogo
                    <span className="text-neutral-500 font-normal ml-2">(Aparecerá al inicio del libro)</span>
                  </label>
                  <Textarea
                    data-testid="prologo-input"
                    placeholder="Escribe el prólogo de tu obra..."
                    value={prologo}
                    onChange={(e) => setPrologo(e.target.value)}
                    rows={8}
                    className="font-serif"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 mb-2 block">
                    Epílogo
                    <span className="text-neutral-500 font-normal ml-2">(Aparecerá al final del libro)</span>
                  </label>
                  <Textarea
                    data-testid="epilogo-input"
                    placeholder="Escribe el epílogo de tu obra..."
                    value={epilogo}
                    onChange={(e) => setEpilogo(e.target.value)}
                    rows={8}
                    className="font-serif"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-900">
                    💡 <strong>Tip:</strong> El prólogo y epílogo aparecerán cuando veas tu proyecto en formato libro. 
                    Haz clic en <strong>"Ver como Libro"</strong> para visualizar cómo quedará.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="notas">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-neutral-900">Notas del Proyecto</h2>
                <Dialog open={dialogNota} onOpenChange={setDialogNota}>
                  <DialogTrigger asChild>
                    <Button data-testid="crear-nota-btn" size="sm" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Nueva Nota
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crear Nota</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Input
                        data-testid="nota-titulo-input"
                        placeholder="Título de la nota"
                        value={nuevaNota.titulo}
                        onChange={(e) => setNuevaNota({ ...nuevaNota, titulo: e.target.value })}
                      />
                      <Textarea
                        data-testid="nota-contenido-input"
                        placeholder="Contenido de la nota..."
                        value={nuevaNota.contenido}
                        onChange={(e) => setNuevaNota({ ...nuevaNota, contenido: e.target.value })}
                        rows={5}
                      />
                      <Button data-testid="guardar-nota-btn" onClick={crearNota} className="w-full">
                        Crear
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {notas.length === 0 ? (
                <p className="text-neutral-500 text-center py-8">No hay notas aún</p>
              ) : (
                <div className="space-y-4">
                  {notas.map((nota) => (
                    <div key={nota.id} data-testid={`nota-item-${nota.id}`} className="border border-neutral-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium text-neutral-900">{nota.titulo}</h3>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button data-testid={`eliminar-nota-btn-${nota.id}`} size="sm" variant="ghost">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar nota?</AlertDialogTitle>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => eliminarNota(nota.id)}>Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                      <p className="text-sm text-neutral-600 whitespace-pre-wrap">{nota.contenido}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="maestros">
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-neutral-900">Guías del Proyecto</h2>
                <Dialog open={dialogMaestro} onOpenChange={setDialogMaestro}>
                  <DialogTrigger asChild>
                    <Button data-testid="crear-maestro-btn" size="sm" className="gap-2">
                      <Plus className="w-4 h-4" />
                      Nueva Guía
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Crear Guía</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <Input
                        data-testid="maestro-titulo-input"
                        placeholder="Título de la guía"
                        value={nuevoMaestro.titulo}
                        onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, titulo: e.target.value })}
                      />
                      <Select
                        value={nuevoMaestro.tipo}
                        onValueChange={(value) => setNuevoMaestro({ ...nuevoMaestro, tipo: value })}
                      >
                        <SelectTrigger data-testid="maestro-tipo-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="personajes">Personajes</SelectItem>
                          <SelectItem value="lugares">Lugares</SelectItem>
                          <SelectItem value="cronologia">Cronología</SelectItem>
                          <SelectItem value="trama">Trama</SelectItem>
                        </SelectContent>
                      </Select>
                      <Textarea
                        data-testid="maestro-contenido-input"
                        placeholder="Contenido de la guía..."
                        value={nuevoMaestro.contenido}
                        onChange={(e) => setNuevoMaestro({ ...nuevoMaestro, contenido: e.target.value })}
                        rows={5}
                      />
                      <Button data-testid="guardar-maestro-btn" onClick={crearMaestro} className="w-full">
                        Crear
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {maestros.length === 0 ? (
                <p className="text-neutral-500 text-center py-8">No hay guías aún</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {maestros.map((maestro) => (
                    <div key={maestro.id} data-testid={`maestro-item-${maestro.id}`} className="border border-neutral-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-medium text-neutral-900">{maestro.titulo}</h3>
                          <span className="text-xs text-neutral-500 uppercase">{maestro.tipo}</span>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button data-testid={`eliminar-maestro-btn-${maestro.id}`} size="sm" variant="ghost">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar guía?</AlertDialogTitle>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => eliminarMaestro(maestro.id)}>Eliminar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                      <p className="text-sm text-neutral-600 whitespace-pre-wrap">{maestro.contenido}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <Dialog open={dialogCapitulo.abierto} onOpenChange={(open) => setDialogCapitulo({ abierto: open, borradorId: dialogCapitulo.borradorId })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Capítulo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                data-testid="capitulo-titulo-input"
                placeholder="Título del capítulo"
                value={nuevoCapitulo.titulo}
                onChange={(e) => setNuevoCapitulo({ titulo: e.target.value })}
              />
              <Button data-testid="guardar-capitulo-btn" onClick={crearCapitulo} className="w-full">
                Crear
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={dialogEscena.abierto} onOpenChange={(open) => setDialogEscena({ abierto: open, capituloId: dialogEscena.capituloId })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Crear Escena</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                data-testid="escena-titulo-input"
                placeholder="Título de la escena"
                value={nuevaEscena.titulo}
                onChange={(e) => setNuevaEscena({ titulo: e.target.value })}
              />
              <Button data-testid="guardar-escena-btn" onClick={crearEscena} className="w-full">
                Crear y Editar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const VistaLibro = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proyecto, setProyecto] = useState(null);
  const [contenidoCompleto, setContenidoCompleto] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    cargarLibro();
  }, [id]);

  const cargarLibro = async () => {
    try {
      setCargando(true);
      const [proyectoRes, borradoresRes] = await Promise.all([
        axios.get(`${API}/proyectos/${id}`),
        axios.get(`${API}/borradores/proyecto/${id}`),
      ]);
      
      setProyecto(proyectoRes.data);
      const borradores = borradoresRes.data;
      
      // Cargar toda la estructura del libro
      const contenido = [];
      
      for (const borrador of borradores) {
        const capitulosRes = await axios.get(`${API}/capitulos/borrador/${borrador.id}`);
        const capitulos = capitulosRes.data;
        
        for (const capitulo of capitulos) {
          const escenasRes = await axios.get(`${API}/escenas/capitulo/${capitulo.id}`);
          const escenas = escenasRes.data;
          
          contenido.push({
            tipo: 'capitulo',
            titulo: capitulo.titulo,
            escenas: escenas
          });
        }
      }
      
      setContenidoCompleto(contenido);
      setCargando(false);
    } catch (e) {
      toast.error("Error al cargar el libro");
      setCargando(false);
    }
  };

  if (cargando) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="text-neutral-600">Preparando tu libro...</div>
      </div>
    );
  }

  if (!proyecto) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      {/* Barra superior fija */}
      <div className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-neutral-200 z-50 px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button 
            data-testid="cerrar-libro-btn" 
            variant="ghost" 
            onClick={() => navigate(`/proyecto/${id}`)}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Cerrar Libro
          </Button>
          <div className="text-sm text-neutral-600 font-serif">{proyecto.titulo}</div>
        </div>
      </div>

      {/* Contenedor del libro */}
      <div className="pt-20 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          
          {/* PORTADA */}
          <div className="bg-white rounded-lg shadow-2xl p-16 mb-8 min-h-[600px] flex flex-col items-center justify-center border border-neutral-200">
            <div className="text-center space-y-8">
              <div className="w-20 h-1 bg-gradient-to-r from-amber-600 to-orange-600 mx-auto"></div>
              <h1 className="text-5xl font-bold text-neutral-900 font-serif leading-tight">
                {proyecto.titulo}
              </h1>
              {proyecto.autor && (
                <p className="text-xl text-neutral-600 font-serif italic">
                  por {proyecto.autor}
                </p>
              )}
              {proyecto.descripcion && (
                <p className="text-base text-neutral-500 max-w-md mx-auto leading-relaxed mt-8">
                  {proyecto.descripcion}
                </p>
              )}
              <div className="w-20 h-1 bg-gradient-to-r from-amber-600 to-orange-600 mx-auto mt-8"></div>
            </div>
          </div>

          {/* PRÓLOGO */}
          {proyecto.prologo && (
            <div className="bg-white rounded-lg shadow-xl p-12 mb-8 border border-neutral-200">
              <h2 className="text-3xl font-bold text-neutral-900 text-center mb-8 font-serif">
                Prólogo
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap text-justify font-serif">
                  {proyecto.prologo}
                </p>
              </div>
            </div>
          )}

          {/* CAPÍTULOS Y ESCENAS */}
          {contenidoCompleto.map((item, index) => (
            <div key={index} className="bg-white rounded-lg shadow-xl p-12 mb-8 border border-neutral-200">
              {/* Título del capítulo */}
              <div className="text-center mb-12">
                <div className="text-sm text-neutral-500 uppercase tracking-widest mb-2 font-sans">
                  Capítulo {index + 1}
                </div>
                <h2 className="text-3xl font-bold text-neutral-900 font-serif">
                  {item.titulo}
                </h2>
                <div className="w-16 h-0.5 bg-amber-600 mx-auto mt-4"></div>
              </div>

              {/* Escenas del capítulo */}
              <div className="space-y-8">
                {item.escenas.map((escena, escenaIndex) => (
                  <div key={escena.id}>
                    {escena.titulo && (
                      <h3 className="text-xl font-semibold text-neutral-800 mb-4 font-serif">
                        {escena.titulo}
                      </h3>
                    )}
                    <div className="prose prose-lg max-w-none">
                      {escena.contenido.split('\n').map((parrafo, pIndex) => (
                        parrafo.trim() && (
                          <p 
                            key={pIndex} 
                            className="text-neutral-700 leading-relaxed mb-4 text-justify indent-8 font-serif"
                          >
                            {parrafo}
                          </p>
                        )
                      ))}
                    </div>
                    {escenaIndex < item.escenas.length - 1 && (
                      <div className="text-center my-8">
                        <div className="inline-block">
                          <span className="text-neutral-400">* * *</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* EPÍLOGO */}
          {proyecto.epilogo && (
            <div className="bg-white rounded-lg shadow-xl p-12 mb-8 border border-neutral-200">
              <h2 className="text-3xl font-bold text-neutral-900 text-center mb-8 font-serif">
                Epílogo
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap text-justify font-serif">
                  {proyecto.epilogo}
                </p>
              </div>
            </div>
          )}

          {/* PÁGINA FINAL */}
          <div className="bg-white rounded-lg shadow-xl p-16 min-h-[400px] flex flex-col items-center justify-center border border-neutral-200">
            <div className="text-center space-y-4">
              <div className="text-4xl font-serif text-neutral-900">Fin</div>
              {proyecto.autor && (
                <p className="text-neutral-500 font-serif italic">— {proyecto.autor}</p>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const EditorEscena = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [escena, setEscena] = useState(null);
  const [contenido, setContenido] = useState("");
  const [titulo, setTitulo] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    cargarEscena();
  }, [id]);

  const cargarEscena = async () => {
    try {
      const response = await axios.get(`${API}/escenas/${id}`);
      setEscena(response.data);
      setContenido(response.data.contenido);
      setTitulo(response.data.titulo);
    } catch (e) {
      toast.error("Error al cargar escena");
    }
  };

  const guardarEscena = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/escenas/${id}`, {
        titulo,
        contenido,
      });
      toast.success("Escena guardada");
      cargarEscena();
    } catch (e) {
      toast.error("Error al guardar escena");
    } finally {
      setGuardando(false);
    }
  };

  if (!escena) return <div className="flex items-center justify-center min-h-screen">Cargando...</div>;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-4xl mx-auto p-8">
        <div className="mb-6 flex items-center justify-between">
          <Button data-testid="volver-editor-btn" variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <Button data-testid="guardar-escena-editor-btn" onClick={guardarEscena} disabled={guardando} className="gap-2">
            <Save className="w-4 h-4" />
            {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-8">
          <Input
            data-testid="escena-titulo-editor"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Título de la escena"
            className="text-2xl font-semibold mb-6 border-0 px-0 focus-visible:ring-0"
          />
          <Textarea
            data-testid="escena-contenido-editor"
            value={contenido}
            onChange={(e) => setContenido(e.target.value)}
            placeholder="Escribe tu escena aquí..."
            className="min-h-[500px] border-0 px-0 focus-visible:ring-0 text-base leading-relaxed resize-none"
          />
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/proyecto/:id" element={<ProyectoDetalle />} />
          <Route path="/libro/:id" element={<VistaLibro />} />
          <Route path="/escena/:id" element={<EditorEscena />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
